import dayjs, { Dayjs, OpUnitType, QUnitType } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import { get } from 'lodash';
import * as momentTimezone from 'moment-timezone';
import { IDateRangePickerDate } from '../shared/components/date-range-picker/date-range-picker.interface';
import { IDropdownOption } from '../shared/components/mat-dropdown/mat-dropdown.component';
import { UserEffects } from '../state/user/user.effects';
import { EDateUnits, IDateUnits } from './date-utilities.interface';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);
dayjs.extend(weekday);
dayjs.extend(isoWeek);

export const DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const HOUR_MINUTE_SECONDS_STRING_REGEX = /^(\d{1,4}):([0-5]\d):([0-5]\d)$/;
export const MILLISECONDS_IN_A_SECOND = 1000;
export const MILLISECONDS_IN_A_MINUTE = MILLISECONDS_IN_A_SECOND * 60;
export const MILLISECONDS_IN_AN_HOUR = MILLISECONDS_IN_A_MINUTE * 60;
export const MILLISECONDS_IN_A_DAY = MILLISECONDS_IN_AN_HOUR * 24;
export const MILLISECONDS_IN_A_MONTH = MILLISECONDS_IN_A_DAY * 30;
export const MILLISECONDS_IN_A_YEAR = MILLISECONDS_IN_A_DAY * 365;
export const datePickerRangeOptions = [
  'today',
  'yesterday',
  'last7Days',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
] as const;

export type TDatePickerRangeOption = (typeof datePickerRangeOptions)[number];
export const dateRangeOptionValues: Record<TDatePickerRangeOption, () => [Dayjs, Dayjs]> = {
  last7Days: () => [dayjs.tz().subtract(6, 'days').startOf('day'), dayjs.tz().endOf('day')],
  lastMonth: () => [dayjs.tz().subtract(1, 'month').startOf('month'), dayjs.tz().subtract(1, 'month').endOf('month')],
  lastWeek: () => [dayjs.tz().subtract(1, 'week').startOf('week'), dayjs.tz().subtract(1, 'week').endOf('week')],
  lastYear: () => [dayjs.tz().subtract(1, 'year').startOf('year'), dayjs.tz().subtract(1, 'year').endOf('year')],
  thisMonth: () => [dayjs.tz().startOf('month'), dayjs.tz().endOf('month')],
  thisWeek: () => [dayjs.tz().startOf('week'), dayjs.tz().endOf('week')],
  thisYear: () => [dayjs.tz().startOf('year'), dayjs.tz().endOf('year')],
  today: () => [dayjs.tz().startOf('d'), dayjs.tz().endOf('d')],
  yesterday: () => [dayjs.tz().subtract(1, 'days').startOf('d'), dayjs.tz().subtract(1, 'days').endOf('d')],
} as const;

export class DateUtilitiesService {
  public static getTimezoneOptions(): IDropdownOption<string>[] {
    return momentTimezone.tz.names().reduce((names: IDropdownOption<string>[], name: string) => {
      if (name.includes('/')) {
        names.push({ id: name, name });
      }

      return names;
    }, []);
  }

  public static convertUserDateToUTCString(date: Dayjs): string {
    if (!get(date, '$x.$timezone')) {
      return dayjs.tz(date.format(API_DATE_FORMAT)).utc().format(API_DATE_FORMAT);
    }

    return date.utc().format(API_DATE_FORMAT);
  }

  public static convertUserDateStringToUTCString(date: string): string {
    return dayjs.tz(date, UserEffects.getUser().configuration.timezone).utc().format(API_DATE_FORMAT);
  }

  public static convertUTCToUserFormatted<ReturnType extends string | null>(
    dateToFormat: ReturnType,
    includeTime?: boolean,
    format?: string,
  ): ReturnType {
    if (dateToFormat === null) {
      return null as ReturnType;
    }

    const { timezone: userTimezone, dateFormat } = UserEffects.getUser().configuration;

    return dayjs
      .tz(dateToFormat, 'UTC')
      .tz(userTimezone)
      .format(format ? format : `${dateFormat}${includeTime ? ' LT' : ''}`) as ReturnType;
  }

  public static convertUTCToUserTimezone(dateToFormat: string): Dayjs {
    return dayjs.tz(dateToFormat, 'UTC').tz(UserEffects.getUser().configuration.timezone);
  }

  public static buildDayjsFromISO8601DateString(dateString: string): Dayjs {
    return dayjs(dateString);
  }

  public static getNowInUTC(): Dayjs {
    return dayjs.utc();
  }

  public static getDayJsFromString(dateString: string): Dayjs {
    return dayjs.utc(dateString);
  }

  public static getDiff(startedAt: string, endedAt: string | null, unit: QUnitType | OpUnitType = 'second'): number {
    const start = DateUtilitiesService.getDayJsFromString(startedAt);
    const end = endedAt ? DateUtilitiesService.getDayJsFromString(endedAt) : DateUtilitiesService.getNowInUTC();

    return end.diff(start, unit);
  }

  public static calculateTimeDifferenceInMilliseconds(date1: Dayjs, date2: Dayjs): number {
    return date1.diff(date2);
  }

  public static getTimeUnitsInMilliseconds<U extends EDateUnits[]>(
    milliseconds: number,
    units: U,
  ): Pick<IDateUnits, U[number]> {
    const timeUnitMap = {
      [EDateUnits.years]: MILLISECONDS_IN_A_YEAR,
      [EDateUnits.months]: MILLISECONDS_IN_A_MONTH,
      [EDateUnits.days]: MILLISECONDS_IN_A_DAY,
      [EDateUnits.hours]: MILLISECONDS_IN_AN_HOUR,
      [EDateUnits.minutes]: MILLISECONDS_IN_A_MINUTE,
      [EDateUnits.seconds]: MILLISECONDS_IN_A_SECOND,
    };

    const result = {} as Pick<IDateUnits, U[number]>;

    let remainingMilliseconds = milliseconds;

    for (const unit of units) {
      const unitValue = Math.floor(remainingMilliseconds / timeUnitMap[unit]);
      result[unit as keyof typeof result] = unitValue;

      remainingMilliseconds = remainingMilliseconds % timeUnitMap[unit];
    }

    return result;
  }

  public static isDateLaterThenDate(date1: Dayjs, date2: Dayjs): boolean {
    return date1.isAfter(date2);
  }

  public static convertRangeIntoDatePickerOption(
    range: IDateRangePickerDate | null | undefined,
  ): TDatePickerRangeOption | null {
    if (!range) {
      return null;
    }

    for (const rangeOption of datePickerRangeOptions) {
      const value = dateRangeOptionValues[rangeOption]();

      if (value[0].unix() === range.startDate?.unix() && value[1].unix() === range.endDate?.unix()) {
        return rangeOption;
      }
    }

    return null;
  }

  public static formatSecondsToTimer(timeAsSeconds: number): string {
    const { hours, minutes, seconds } = DateUtilitiesService.getTimeUnitsInMilliseconds(timeAsSeconds * 1000, [
      EDateUnits.hours,
      EDateUnits.minutes,
      EDateUnits.seconds,
    ]);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  public static timeStringToSeconds(time: string | null | undefined): number | null {
    if (!time) {
      return null;
    }

    const match: RegExpMatchArray | null = time.match(HOUR_MINUTE_SECONDS_STRING_REGEX);

    if (!match) {
      return null;
    }

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3]);

    return hours * 3600 + minutes * 60 + seconds;
  }
}
